import type { Shift, ScheduledShift, ScheduledShiftDisplayItem } from "../types";

/**
 * Convert an actual `Shift` (a real clock-in/out record) into a
 * `ScheduledShiftDisplayItem` so it can be rendered by the scheduled-shift
 * list without any changes to the card component.
 *
 * Key mappings:
 *   - `planned_start_at` ← manual_arrival_time ?? started_at
 *   - `planned_end_at`   ← manual_end_time ?? ended_at ?? "" (empty = open)
 *   - `status`           ← "unscheduled"
 *   - `actual_shift_id`  ← shift.id  (self-referential: allows isOpen detection)
 *   - `_unscheduled`     ← true
 */
export function actualShiftToDisplayItem(shift: Shift): ScheduledShiftDisplayItem {
  const startAt = shift.manual_arrival_time ?? shift.started_at;
  const endAt = shift.manual_end_time ?? shift.ended_at ?? "";

  return {
    id: shift.id,
    organization_id: 0,
    user_id: shift.employee_id,
    location_id: null,
    schedule_id: null,
    planned_start_at: startAt,
    planned_end_at: endAt,
    timezone: shift.timezone ?? "UTC",
    status: "unscheduled",
    notes: shift.notes,
    actual_shift_id: shift.id,
    created_by_user_id: null,
    updated_by_user_id: null,
    created_at: shift.created_at,
    updated_at: shift.updated_at,
    _unscheduled: true,
    kind: "shift",
  };
}

/**
 * Given a list of scheduled shifts (with their linked `actual_shift_id`s) and
 * a list of actual shifts, return only those actual shifts that are **not**
 * already linked to a scheduled shift in the provided list.
 *
 * An actual shift is "unscheduled" when none of the scheduled shifts in the
 * range references it via `actual_shift_id`.
 */
export function filterUnscheduledShifts(
  actualShifts: Shift[],
  scheduledShifts: ScheduledShift[]
): Shift[] {
  const linkedIds = new Set(
    scheduledShifts
      .map((s) => s.actual_shift_id)
      .filter((id): id is number => id != null)
  );
  return actualShifts.filter((s) => !linkedIds.has(s.id));
}

/**
 * Merge scheduled shifts (as `ScheduledShiftDisplayItem`) and unscheduled
 * actual shifts (adapted) into a single list sorted by start time ascending.
 */
export function mergeAndSortDisplayItems(
  scheduledShifts: ScheduledShift[],
  unscheduledShifts: Shift[],
  comparisonSourceShifts: Shift[] = unscheduledShifts
): ScheduledShiftDisplayItem[] {
  const actualById = new Map(comparisonSourceShifts.map((shift) => [shift.id, shift]));
  const scheduled: ScheduledShiftDisplayItem[] = scheduledShifts.map((shift) => {
    const linkedActual =
      shift.actual_shift_id != null ? actualById.get(shift.actual_shift_id) : undefined;
    return {
      ...shift,
      scheduled_comparison:
        shift.scheduled_comparison ?? linkedActual?.scheduled_comparison ?? null,
      kind: "shift",
    };
  });
  const adapted = unscheduledShifts.map(actualShiftToDisplayItem);
  const all = [...scheduled, ...adapted];
  all.sort(
    (a, b) =>
      Date.parse(a.planned_start_at) - Date.parse(b.planned_start_at)
  );
  return all;
}
